import {
  Badge,
  SidebarDropdownMenuItem,
  Skeleton,
} from "@design-system/components";
import type { BadgeProps } from "@design-system/components";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Gauge } from "lucide-react";
import React from "react";

import { useSystemStatus } from "../queries";
import type { SystemStatus } from "../schemas";

const STATUS_SEMANTIC_MAP: Record<
  SystemStatus["indicator"],
  BadgeProps["semantic"]
> = {
  none: "success",
  minor: "warning",
  major: "critical",
  critical: "critical",
};

const STATUS_LABEL_MAP: () => Record<
  SystemStatus["indicator"],
  string
> = () => ({
  none: t`All operational`,
  minor: t`Minor issues`,
  major: t`Major issues`,
  critical: t`Critical issues`,
});

function SystemStatusItem() {
  const { status, pageUrl, isLoading } = useSystemStatus();

  const indicator = status?.indicator ?? "none";

  const content = (
    <>
      <Gauge />
      <span className="grow">
        <Trans>Status</Trans>
      </span>
      {isLoading ? (
        <Skeleton className="h-5 w-20" />
      ) : (
        <Badge
          semantic={STATUS_SEMANTIC_MAP[indicator]}
          emphasis={indicator === "critical" ? "emphasised" : "default"}
        >
          {STATUS_LABEL_MAP()[indicator]}
        </Badge>
      )}
    </>
  );

  if (pageUrl) {
    return (
      <SidebarDropdownMenuItem asChild>
        <a
          href={pageUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-track-name="system-status"
          data-track-context="help-menu"
        >
          {content}
        </a>
      </SidebarDropdownMenuItem>
    );
  }

  return (
    <SidebarDropdownMenuItem
      data-track-name="system-status"
      data-track-context="help-menu"
    >
      {content}
    </SidebarDropdownMenuItem>
  );
}

export { SystemStatusItem };
